import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getGalleriesByEvent, updateEventGallery, archiveEventGallery } from "../../services/galleryService";
import Loader from "../../components/common/Loader";
import Button from "../../components/common/Button";
import CreateGalleryModal from "../../components/common/CreateGalleryModal";
import ConfirmModal from "../../components/common/ConfirmModal";
import EditNameModal from "../../components/common/EditNameModal";
import { toast } from "react-toastify";

const RoomGalleries = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const room = state?.room;

  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateGallery, setShowCreateGallery] = useState(false);
  const [editGalleryTarget, setEditGalleryTarget] = useState(null);
  const [deleteGalleryTarget, setDeleteGalleryTarget] = useState(null);
  const [editGalleryName, setEditGalleryName] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  // Use EventId from room data (same as ChatRoomId in the API)
  const eventId = room?.EventId || room?.Id || roomId;

  useEffect(() => {
    getGalleriesByEvent(eventId)
      .then((res) => setGalleries(res.data?.Result || []))
      .catch(() => toast.error("Failed to load galleries."))
      .finally(() => setLoading(false));
  }, [eventId]);

  const refreshGalleries = () => {
    getGalleriesByEvent(eventId)
      .then((res) => setGalleries(res.data?.Result || []))
      .catch(() => toast.error("Failed to load galleries."));
  };

  const handleGalleryClick = (gallery) => {
    navigate(`/rooms/${roomId}/gallery/${gallery.Id}`, {
      state: { gallery, backPath: `/rooms/${roomId}/galleries`, room },
    });
  };

  const openEditGalleryModal = (gallery) => {
    setEditGalleryTarget(gallery);
    setEditGalleryName(gallery?.Name || "");
  };

  const handleEditGallery = async () => {
    const trimmed = editGalleryName.trim();
    if (!trimmed) {
      toast.warning("Gallery name is required.");
      return;
    }

    setActionLoading(true);
    try {
      await updateEventGallery(editGalleryTarget.Id, {
        EventId: Number(eventId),
        Name: trimmed,
      });
      toast.success("Gallery updated successfully");
      setEditGalleryTarget(null);
      refreshGalleries();
    } catch (err) {
      toast.error(err.response?.data?.Message || "Failed to update gallery.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteGallery = async () => {
    if (!deleteGalleryTarget) return;

    setActionLoading(true);
    try {
      await archiveEventGallery({
        galleryId: deleteGalleryTarget.Id,
        eventId,
        name: deleteGalleryTarget.Name,
      });
      toast.success("Gallery deleted");
      setDeleteGalleryTarget(null);
      refreshGalleries();
    } catch (err) {
      toast.error(err.response?.data?.Message || "Failed to delete gallery.");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div>
      <Button variant="secondary" onClick={() => navigate("/rooms")} style={{ marginBottom: 20 }}>
        ← Back to Rooms
      </Button>

      {/* Room Info Header */}
      {room && (
        <div style={styles.roomHeader}>
          {room.LogoUrl ? (
            <img
              src={room.LogoUrl.replace("\\", "/")}
              alt={room.Name}
              style={styles.avatar}
              onError={(e) => { e.target.style.display = "none"; }}
            />
          ) : (
            <div style={styles.avatarFallback}>
              {room.Name?.charAt(0).toUpperCase() || "R"}
            </div>
          )}
          <div>
            <h2 style={styles.roomName}>{room.Name}</h2>
            <div style={styles.roomMeta}>
              <span>👥 {room.TotalUsers} users</span>
              <span>🎉 {room.TotalEvents} events</span>
              <span style={styles.roomTypeBadge}>{room.RoomType}</span>
            </div>
          </div>
        </div>
      )}

      {/* Galleries */}
      <div style={styles.sectionHeader}>
        <h3 style={styles.sectionTitle}>Galleries</h3>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {!loading && (
            <span style={styles.sectionCount}>{galleries.length} found</span>
          )}
          <Button onClick={() => setShowCreateGallery(true)} style={{ padding: "6px 14px", fontSize: 13 }}>
            + New Gallery
          </Button>
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : galleries.length === 0 ? (
        <p style={{ color: "#888", fontSize: 14 }}>No galleries found for this room.</p>
      ) : (
        <div style={styles.galleryGrid}>
          {galleries.map((gallery) => (
            <div
              key={gallery.Id}
              style={styles.galleryCard}
              onClick={() => handleGalleryClick(gallery)}
            >
              <div style={{ ...styles.colorBar, background: gallery.IsPremium ? "#f59e0b" : "#6c63ff" }} />
              <div style={styles.galleryBody}>
                <div style={styles.galleryTopRow}>
                  <span style={styles.galleryName}>{gallery.Name}</span>
                  <div style={styles.galleryActions}>
                    {gallery.IsPremium && <span style={styles.premiumBadge}>⭐ Premium</span>}
                    <button
                      style={styles.cardActionBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditGalleryModal(gallery);
                      }}
                      title="Edit gallery name"
                    >
                      ✏
                    </button>
                    <button
                      style={{ ...styles.cardActionBtn, ...styles.deleteActionBtn }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteGalleryTarget(gallery);
                      }}
                      title="Delete gallery"
                    >
                      🗑
                    </button>
                  </div>
                </div>
                <div style={styles.galleryCounts}>
                  <span>🖼️ {gallery.MediaCount} media</span>
                  <span>📁 {gallery.AlbumCount} albums</span>
                </div>
                {gallery.Package && (
                  <div style={styles.packageName}>📦 {gallery.Package.Name}</div>
                )}
                <div style={styles.galleryMeta}>
                  {gallery.IsWatermark && <span style={styles.tag}>Watermark</span>}
                  {gallery.IsMediaHandlesGallery && <span style={styles.tag}>Media Handles</span>}
                  {gallery.IsUserGallery && <span style={styles.tag}>User Gallery</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreateGallery && (
        <CreateGalleryModal
          eventId={eventId}
          onClose={() => setShowCreateGallery(false)}
          onCreated={() => {
            refreshGalleries();
          }}
        />
      )}

      {editGalleryTarget && (
        <EditNameModal
          title="Edit Gallery Name"
          label="Gallery Name"
          value={editGalleryName}
          onChange={setEditGalleryName}
          onSave={handleEditGallery}
          onCancel={() => !actionLoading && setEditGalleryTarget(null)}
          loading={actionLoading}
          saveText="Save"
        />
      )}

      {deleteGalleryTarget && (
        <ConfirmModal
          title="Delete Gallery"
          message={`Delete gallery "${deleteGalleryTarget.Name}"?`}
          confirmText="🗑"
          danger
          loading={actionLoading}
          onConfirm={handleDeleteGallery}
          onCancel={() => !actionLoading && setDeleteGalleryTarget(null)}
        />
      )}
    </div>
  );
};

const styles = {
  roomHeader: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    background: "#fff",
    borderRadius: 14,
    padding: "18px 24px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
    marginBottom: 28,
    maxWidth: 560,
  },
  avatar: {
    width: 56,
    height: 56,
    borderRadius: "50%",
    objectFit: "cover",
    border: "2px solid #ede9ff",
    flexShrink: 0,
  },
  avatarFallback: {
    width: 56,
    height: 56,
    borderRadius: "50%",
    background: "#6c63ff",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: 22,
    flexShrink: 0,
  },
  roomName: { margin: "0 0 6px", fontSize: 18, fontWeight: 700, color: "#1e1e2d" },
  roomMeta: { display: "flex", gap: 14, fontSize: 13, color: "#666", flexWrap: "wrap" },
  roomTypeBadge: {
    background: "#ede9ff", color: "#6c63ff",
    padding: "1px 10px", borderRadius: 20,
    fontWeight: 600, fontSize: 12,
  },

  sectionHeader: { display: "flex", alignItems: "center", gap: 12, margin: "0 0 16px" },
  sectionTitle: { margin: 0, fontSize: 18, fontWeight: 700, color: "#1e1e2d" },
  sectionCount: { fontSize: 13, color: "#888", background: "#f0f0f0", padding: "2px 10px", borderRadius: 20 },

  galleryGrid: { display: "flex", flexWrap: "wrap", gap: 16 },
  galleryCard: {
    background: "#fff",
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
    cursor: "pointer",
    width: 240,
    transition: "transform 0.15s, box-shadow 0.15s",
  },
  colorBar: { height: 5 },
  galleryBody: { padding: "14px 16px 16px" },
  galleryTopRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 },
  galleryActions: { display: "flex", alignItems: "center", gap: 6 },
  galleryName: { fontWeight: 700, fontSize: 14, color: "#1e1e2d", flex: 1, marginRight: 8 },
  premiumBadge: {
    fontSize: 10, fontWeight: 700,
    background: "#fef3c7", color: "#d97706",
    padding: "2px 8px", borderRadius: 20, whiteSpace: "nowrap",
  },
  galleryCounts: { display: "flex", gap: 12, fontSize: 12, color: "#555", marginBottom: 8 },
  packageName: { fontSize: 12, color: "#6c63ff", marginBottom: 8 },
  galleryMeta: { display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 },
  cardActionBtn: {
    border: "1px solid #d1d5db",
    background: "#fff",
    color: "#374151",
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 600,
    padding: "2px 7px",
    cursor: "pointer",
  },
  deleteActionBtn: {
    borderColor: "#fecaca",
    color: "#b91c1c",
    background: "#fff5f5",
  },
  tag: { fontSize: 10, background: "#f5f5f5", color: "#888", padding: "2px 8px", borderRadius: 20 },
};

export default RoomGalleries;
